import { CloseOutlined } from '@ant-design/icons';
import {
  MyLeadsFriendlyName,
  MyLeadsSearchQuery,
  MyLeadsFilterTypes,
} from '@mm/state/interfaces/myLeads';
import { Tag, Tooltip } from 'antd';
import { uniqueId } from 'lodash';
import React, { ReactNode, useMemo } from 'react';
import { useFilterParams } from '../MyLeadsFiltersPanel/use-filter-params';
import styles from './MyLeadsFilterBar.module.css';
import classNames from 'classnames';
import { selectAudienceVariables } from '@mm/state/selectors/audienceSelector';
import { useSelector } from 'react-redux';
import { FilterType, TagsFilters } from '@mm/state/model/AudiencesModel';
import { useMapAudienceFiltersToTags } from '../use-map-audience-filters-to-tags';

export type Tag = {
  key: string | number;
  subKey?: string;
  value: string | boolean | string[];
  friendlyName: string;
  lowercase?: boolean;
  fatTag?: string;
  type: MyLeadsFilterTypes | FilterType | 'tags_filters';
};

type Props = {
  css?: {
    container?: string;
  };
  tagColor?: string;
  withReset?: boolean;
  closable?: boolean;
  onBeforeTagsComponent?: ReactNode;
  filterParams: MyLeadsSearchQuery;
  setFilterParams: (params: MyLeadsSearchQuery) => void;
};

export const MyLeadsFilterBar = ({
  css,
  tagColor = 'geekblue',
  closable = true,
  withReset = true,
  filterParams,
  setFilterParams,
  onBeforeTagsComponent,
}: Props) => {
  const { hasFilters, getUpdatedFilterParams, getUpdatedTagFilters } =
    useFilterParams({
      filterParams,
    });
  const { mapFilterParamsToTags, mapQueryToTags, mapTagFiltersToTags } =
    useMapAudienceFiltersToTags();
  const audienceVariables = useSelector(selectAudienceVariables);

  const mappedFiltersToTags = useMemo(
    () =>
      Object.entries(filterParams.query || {}).flatMap(([key, value]) =>
        mapQueryToTags(value, MyLeadsFilterTypes[key]),
      ),
    [filterParams.query, mapQueryToTags],
  );

  const mappedFilterParamsToTags = useMemo(
    () => mapFilterParamsToTags(filterParams.filterParams, audienceVariables),
    [filterParams.filterParams, audienceVariables, mapFilterParamsToTags],
  );

  const mappedtagFiltersToTags = useMemo(
    () => mapTagFiltersToTags(filterParams.tagsFilters as TagsFilters),
    [filterParams.tagsFilters, mapTagFiltersToTags],
  );

  const tags = [
    ...mappedFiltersToTags,
    ...mappedFilterParamsToTags,
    ...mappedtagFiltersToTags,
  ].filter(Boolean);

  const onTagRemove = (tag: Tag) => {
    let updatedParams;
    if (tag.type === 'tags_filters') {
      const values = Array.isArray(tag.value) ? tag.value : [tag.value];
      updatedParams = getUpdatedTagFilters(values as string[]);
    } else {
      updatedParams = getUpdatedFilterParams({
        key: tag.key,
        value: tag.value,
        isExisting: true,
        type: tag.type as MyLeadsFilterTypes,
      });
    }

    if (tag.subKey) {
      delete updatedParams.query?.attributes?.created_at?.[0][tag.subKey];
    }

    setFilterParams(updatedParams);
  };

  const onResetAll = () => {
    setFilterParams({ query: {} });
  };

  if (!hasFilters) {
    return <></>;
  }

  return (
    <div className={classNames(styles.container, css?.container)}>
      <div className={styles.content}>
        {withReset && Boolean(hasFilters) && (
          <Tag
            color="default"
            closable
            closeIcon={<CloseOutlined className={styles.closeIcon} />}
            onClose={onResetAll}
            onClick={onResetAll}
            className={styles.resetAll}
          >
            Reset All
          </Tag>
        )}
        {onBeforeTagsComponent}
        {tags?.map((tag: Tag) => {
          const key = `${uniqueId(tag.friendlyName)}`;
          return (
            <Tooltip
              title={MyLeadsFriendlyName[tag.key]}
              key={key}
              overlayClassName={styles.tooltip}
              getPopupContainer={triggerNode =>
                triggerNode.parentElement || document.body
              }
            >
              <Tag
                key={key}
                color={tagColor}
                className={classNames(styles.tag, {
                  [styles.lowercase]: tag?.lowercase,
                  [styles.defaultTag]: !tagColor,
                })}
                closable={closable}
                onClose={() => onTagRemove(tag)}
              >
                {tag?.fatTag && (
                  <span className={styles.fatTag}>{tag.fatTag}:</span>
                )}{' '}
                {tag.friendlyName}
              </Tag>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};
